import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ChevronLeft, ChevronRight, Loader2, ImageOff } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { useStorageUrl } from '../chatHome/MemberAvatar';

/**
 * LOOK AT A REFERENCE PROPERLY.
 *
 * Owner 2026-09-13: *"the generated image, upload image on Where it happens, Objects that matter, Your cast
 * should allow enlarge mode viewing to see clearly"*. Right, and the reason is the whole point of generating
 * references early: a plate exists to answer *is this the place I meant?*, and an 80-pixel thumbnail cannot
 * answer it. A creator was being asked to approve something they could not actually see.
 *
 * ONE VIEWER FOR EVERY SURFACE — places, objects, faces and the angles of a character sheet — rather than a
 * viewer per section, because they are all the same act: look closely at a picture the production will be
 * built from.
 *
 * MULTI-IMAGE ON PURPOSE. A character's consistency rests on a SHEET (front, three-quarter, profile), and the
 * question a creator has is whether those angles are the same person — which can only be judged by moving
 * between them at size. So the viewer pages, with the keyboard, and says which one is showing.
 *
 * Built on Radix Dialog, so Escape, click-outside, focus trapping and scroll locking are the library's job
 * rather than ours. That matters here: the studio's own hand-rolled pickers ignored Escape for weeks
 * (BUGS.md 2026-09-12) precisely because they re-implemented what this gives for free.
 */
export interface LightboxImage {
  /** Storage path; resolved to a URL by the viewer. */
  path: string;
  /** What this picture IS, said plainly ("Mara, three-quarter"). */
  caption?: string;
}

export interface ImageLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: LightboxImage[];
  /** Which one to show first. */
  startIndex?: number;
  /** What the set is, for the dialog's accessible name ("Reference for The flooded market"). */
  title: string;
}

/** The picture itself, at the size of the viewport rather than the size of a thumbnail. */
const Frame: React.FC<{ image: LightboxImage }> = ({ image }) => {
  const url = useStorageUrl(image.path);
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [image.path]);

  if (!url) {
    return (
      <span className="flex h-[60vh] w-full items-center justify-center" data-testid="lightbox-loading">
        <Loader2 className="h-5 w-5 animate-spin text-secondary-400" aria-hidden />
      </span>
    );
  }
  if (broken) {
    return (
      <span className={`flex h-[60vh] w-full flex-col items-center justify-center gap-2 ${TYPE.meta} text-secondary-400`} data-testid="lightbox-broken">
        <ImageOff className="h-5 w-5" aria-hidden />
        That picture could not be loaded.
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={image.caption || 'Reference'}
      data-testid="lightbox-image"
      onError={() => setBroken(true)}
      className="max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
    />
  );
};

const ImageLightbox: React.FC<ImageLightboxProps> = ({ open, onOpenChange, images, startIndex = 0, title }) => {
  const [i, setI] = useState(startIndex);
  // Re-open on whichever picture was clicked, rather than wherever the last visit left off.
  useEffect(() => { if (open) setI(Math.min(Math.max(startIndex, 0), Math.max(images.length - 1, 0))); }, [open, startIndex, images.length]);

  const many = images.length > 1;
  const go = (delta: number) => setI((n) => (n + delta + images.length) % images.length);
  const current = images[i];
  if (!current) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/70" />
        <Dialog.Content
          data-testid="image-lightbox"
          aria-label={title}
          // Arrow keys page the sheet: comparing angles is the reason this shows more than one.
          onKeyDown={(e) => {
            if (!many) return;
            if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
          }}
          className="fixed left-1/2 top-1/2 z-[70] flex max-h-[92dvh] w-[94vw] max-w-4xl -translate-x-1/2
                     -translate-y-1/2 flex-col items-center gap-3 focus:outline-none"
        >
          <Dialog.Title className="sr-only">{title}</Dialog.Title>

          <div className="flex w-full items-center justify-center gap-3">
            {many && (
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous picture"
                data-testid="lightbox-prev"
                className="shrink-0 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <Frame image={current} />
            {many && (
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next picture"
                data-testid="lightbox-next"
                className="shrink-0 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* WHICH picture this is. On a character sheet that is the difference between "the face" and "the
              three-quarter angle", which is exactly what is being judged. */}
          <p className={`${TYPE.meta} text-center text-white/80`} data-testid="lightbox-caption">
            {current.caption || title}{many ? ` · ${i + 1} of ${images.length}` : ''}
          </p>

          <Dialog.Close
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute -top-2 right-0 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-4 w-4" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ImageLightbox;
